import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { Announcement, ApiResponse } from '@/types'

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const announcementKeys = {
  all: ['announcements'] as const,
  lists: () => [...announcementKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...announcementKeys.lists(), filters] as const,
  details: () => [...announcementKeys.all, 'detail'] as const,
  detail: (id: number) => [...announcementKeys.details(), id] as const,
}

// ─── Filters ─────────────────────────────────────────────────────────────────

export interface AnnouncementFilters {
  status?: string
  target_type?: string
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useAnnouncements(page = 1, filters: AnnouncementFilters = {}) {
  return useQuery({
    queryKey: announcementKeys.list({ page, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (filters.status) params.status = filters.status
      if (filters.target_type) params.target_type = filters.target_type
      const { data } = await api.get<ApiResponse<Announcement[]>>('/api/announcements', { params })
      return data
    },
  })
}

export function useAnnouncementDetail(id: number) {
  return useQuery({
    queryKey: announcementKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Announcement>>(`/api/announcements/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

interface CreateAnnouncementInput {
  title: string
  body: string
  published_at: string
  expires_at?: string | null
  target_type: string
  target_id?: number | null
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateAnnouncementInput) => {
      const { data } = await api.post<ApiResponse<Announcement>>('/api/announcements', {
        announcement: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, announcementKeys.lists())
    },
  })
}

interface UpdateAnnouncementInput extends Partial<CreateAnnouncementInput> {
  id: number
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateAnnouncementInput) => {
      const { data } = await api.patch<ApiResponse<Announcement>>(`/api/announcements/${id}`, {
        announcement: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, announcementKeys.lists())
      invalidateDetailQueries(queryClient, announcementKeys.detail(data.id))
    },
  })
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/announcements/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, announcementKeys.lists())
    },
  })
}
